'use client';

import type { ZodiacSign } from '@repo/lib';
import { useEffect, useRef } from 'react';
import { useUser } from '@/contexts/user-context';
import { useGuestStore } from '@/lib/stores/guest-store';
import { trpc } from '@/lib/trpc';

const SESSION_STORAGE_KEY = 'lead-capture-birth-data';

// Time format constants
const TIME_FORMAT_HH_MM_LENGTH = 5;

type SessionBirthPayload = {
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
  name?: string;
  timezone?: string;
  birthPlaceTimezone?: string | null;
  normalizedBirthData?: {
    date?: string;
    time?: string;
    timezone?: string;
    place?: string;
  } | null;
};

function readSessionBirthData(): SessionBirthPayload | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as SessionBirthPayload;
  } catch (error) {
    console.warn('[LeadCaptureSync] Failed to parse session birth data', error);
    return null;
  }
}

function clearSessionBirthData() {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

export function LeadCaptureSync() {
  const { userProgress, isLoggedIn, refreshUserProgress } = useUser();
  const onboardingData = useGuestStore((state) => state.onboardingData);
  const clearGuestDataSafely = useGuestStore((state) => state.clearGuestDataSafely);
  const updateProfile = trpc.user.progress.updateProfile.useMutation();
  const hasAttemptedSync = useRef(false);

  useEffect(() => {
    if (!(isLoggedIn && userProgress)) {
      return;
    }

    const payload = readSessionBirthData();
    if (!payload || updateProfile.isPending || hasAttemptedSync.current) {
      return;
    }

    const profileHasBirthData = Boolean(
      userProgress.dob && userProgress.birthTime && userProgress.birthPlace
    );

    if (profileHasBirthData) {
      clearSessionBirthData();
      return;
    }

    const dob =
      payload.birthDate || onboardingData?.dob || onboardingData?.normalizedBirthData?.date;
    const birthTime =
      payload.birthTime ||
      onboardingData?.birthTime ||
      onboardingData?.normalizedBirthData?.time?.slice(0, TIME_FORMAT_HH_MM_LENGTH) ||
      null;
    const birthPlace =
      payload.birthPlace ||
      onboardingData?.birthPlace ||
      onboardingData?.normalizedBirthData?.place;
    const birthPlaceTimezone =
      payload.birthPlaceTimezone ||
      onboardingData?.birthPlaceTimezone ||
      onboardingData?.normalizedBirthData?.timezone ||
      payload.timezone ||
      null;

    if (!(dob && birthTime && birthPlace)) {
      return;
    }

    hasAttemptedSync.current = true;
    updateProfile.mutate(
      {
        dob,
        birthTime,
        birthPlace,
        birthPlaceTimezone,
        name: onboardingData?.name,
        sign: onboardingData?.zodiacSign as ZodiacSign | undefined,
      },
      {
        onSuccess: () => {
          clearSessionBirthData();
          clearGuestDataSafely(true);
          refreshUserProgress();
        },
        onSettled: () => {
          hasAttemptedSync.current = false;
        },
      }
    );
  }, [
    isLoggedIn,
    userProgress,
    onboardingData,
    updateProfile,
    refreshUserProgress,
    clearGuestDataSafely,
  ]);

  return null;
}
